import React, { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Popover, Transition } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

type AwaitingTicketType = 'INCIDENT' | 'CHANGE' | 'SERVICE_REQUEST';

interface AwaitingActivityItem {
  caseId: string;
  number: string;
  ticketType: AwaitingTicketType;
  shortDescription: string;
  priority: string;
  createdAt: string;
  externalNumber: string | null;
  externalTable: string;
}

function ticketHref(item: AwaitingActivityItem): string {
  switch (item.ticketType) {
    case 'INCIDENT':
      return `/incidents/${item.caseId}`;
    case 'CHANGE':
      return `/changes/${item.caseId}`;
    case 'SERVICE_REQUEST':
      return `/service-requests/${item.caseId}`;
    default:
      return '/';
  }
}

function ticketTypeLabel(ticketType: AwaitingTicketType): string {
  switch (ticketType) {
    case 'INCIDENT':
      return 'Incident';
    case 'CHANGE':
      return 'Change';
    case 'SERVICE_REQUEST':
      return 'Service Request';
    default:
      return ticketType;
  }
}

function formatRelativeTime(iso: string): string {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return '';
  const diffMs = Date.now() - created;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatPriority(priority: string): string {
  return priority.replace(/_/g, ' ');
}

/**
 * Global ServiceNow inbound inbox: same count for all non-Contact users.
 * Count drops only when the first Pulse activity is logged on that ticket.
 */
export default function ServiceNowNotificationBell() {
  const { data, isLoading } = useQuery({
    queryKey: ['servicenow-awaiting-activity'],
    queryFn: async () => {
      const response = await api.get('/integrations/servicenow/awaiting-first-activity');
      return response.data?.data as { count: number; items: AwaitingActivityItem[] };
    },
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  const count = data?.count ?? 0;
  const items = data?.items ?? [];
  const badgeLabel = useMemo(() => {
    if (count <= 0) return null;
    return count > 99 ? '99+' : String(count);
  }, [count]);

  return (
    <Popover className="relative mr-2">
      <Popover.Button
        className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-label={count > 0 ? `${count} ServiceNow tickets awaiting activity` : 'ServiceNow notifications'}
      >
        <BellIcon className="h-6 w-6" aria-hidden="true" />
        {badgeLabel && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white">
            {badgeLabel}
          </span>
        )}
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute right-0 z-40 mt-2 w-96 origin-top-right overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
            <span className="text-xs text-gray-500">
              {isLoading ? 'Loading…' : `${count} awaiting activity`}
            </span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {!isLoading && items.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                No ServiceNow tickets awaiting first activity.
              </p>
            )}

            {items.map((item) => (
              <Popover.Button
                as={Link}
                key={item.caseId}
                to={ticketHref(item)}
                className="block border-b border-gray-50 px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {item.externalNumber || item.number} created from ServiceNow
                    </p>
                    <p className="mt-0.5 truncate text-xs text-gray-500">
                      {ticketTypeLabel(item.ticketType)}
                      {' • '}
                      {formatPriority(item.priority)}
                      {' • '}
                      {item.shortDescription}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">{formatRelativeTime(item.createdAt)}</p>
                  </div>
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
                </div>
              </Popover.Button>
            ))}
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
